import {EditorConfig} from '@mintdata/mint-kit/api/decorators/Editor';
import {IPropertyConfig} from '@mintdata/mint-kit/api/properties/PropertyConfig';
import {IPropertyConverter} from '@mintdata/mint-kit/api/properties/PropertyConverter';
import {PropertyMap} from '@mintdata/mint-kit/api/properties/PropertyMap';
import {PropertySpec, PropertyTypeConstructor, PropName} from '@mintdata/mint-kit/api/properties/PropertySpec';
import {
    PropertyConverterConstructor,
    PropertyEditorConstructor,
} from '@mintdata/mint-kit/api/properties/PropertyTypeConfig';
import {PropertyUtils} from '@mintdata/mint-kit/api/properties/PropertyUtils';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {serialize} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';
import {observer} from 'mobx-react';

export class PropConfig<T> implements IPropertyConfig<T>
{
    public readonly spec:PropertySpec<T>;
    public readonly defaultValue:T;
    public readonly defaultValueJson:any;
    public readonly propertiesConfigs:Map<PropName, PropConfig<any>> = new Map<PropName, PropConfig<any>>();
    public readonly editor:PropertyEditorConstructor<T>;
    public readonly editorConfig:EditorConfig;

    public readonly type:Type;

    // this array is needed for property order to be deterministic
    public readonly propertyNames:Array<PropName>;
    public readonly boundable:boolean;
    public readonly isPrimitive:boolean;

    public readonly isReadonly:boolean = false;
    public readonly isDisabled:boolean = false;

    public static readonly editorObservers:Map<PropertyEditorConstructor<any>, PropertyEditorConstructor<any>> = new Map<PropertyEditorConstructor<any>, PropertyEditorConstructor<any>>();

    public constructor(spec:PropertySpec<T>, defaultValue:T)
    {
        this.spec = spec;
        this.defaultValue = defaultValue;
        this.defaultValueJson = defaultValue ? serialize(defaultValue) : null;

        const valueInstance = this.createDefaultValue();

        this.type = Type.isInstanceOfIValue(valueInstance)
            ? (valueInstance as any as IValue).getType()
            : null;

        if( spec && spec.typeConfig && spec.typeConfig.editor && !PropConfig.editorObservers.has(spec.typeConfig.editor) )
            PropConfig.editorObservers.set(spec.typeConfig.editor, observer(spec.typeConfig.editor));

        this.editor = spec && spec.typeConfig && spec.typeConfig.editor
            ? PropConfig.editorObservers.get(spec.typeConfig.editor)
            : null;

        this.editorConfig = spec && spec.typeConfig && spec.typeConfig.editor
            ? PropertyUtils.getEditorConfig(spec.typeConfig.editor)
            : new EditorConfig(null);

        if( !this.editorConfig )
            this.editorConfig = new EditorConfig(null);

        PropConfig.fillPropsConfigs(this.propertiesConfigs, valueInstance);

        this.propertyNames = PropConfig.getPropertyNames(valueInstance);

        this.isPrimitive = this.propertiesConfigs.size == 0 ||
            (this.propCtor && typeof this.propCtor.coerce === 'function');

        // currently we can only bound primitives or "simple composites" (i.e. composites of primitives) or Arrays of those
        this.boundable = this.isPrimitive ||
            Array.from(this.propertiesConfigs.values()).every(subConfig => subConfig.isPrimitive);
    }

    public get name():PropName
    {
        return this.spec ? this.spec.name : null;
    }

    public get title():string
    {
        return this.spec ? this.spec.title : this.name;
    }

    public get converterCtor():PropertyConverterConstructor<T>
    {
        return this.spec && this.spec.typeConfig ? this.spec.typeConfig.converter : null;
    }

    public get isRequired():boolean
    {
        return this.spec && this.spec.typeConfig ? this.spec.isRequired : false;
    }

    public get isArray():boolean
    {
        return this.spec && this.spec.typeConfig ? this.spec.typeConfig.isArray : false;
    }

    public get isComposite():boolean
    {
        return !this.isPrimitive;
    }

    // number of fields basically
    public get width():number
    {
        return this.isPrimitive ? 1 : this.propertyNames.length;
    }

    public get typeCtor():PropertyTypeConstructor<T>
    {
        return this.spec && this.spec ? this.spec.typeCtor : null;
    }

    // this thing basically shows how many "primitive" values are needed to compose the full value for this object
    //  i.e. for primitives, this is 1, for composites => it's equal to the fields count,
    //  BUT only if all fields are primitives,
    //  otherwise => this prop shouldn't be bound at all (see this.boundable comment in constructor)
    public get valueWidth():number
    {
        return this.isComposite ? this.propertiesConfigs.size : 1;
    }

    // this exist just for the sake of naming consistency on destructuring
    public get propCtor():PropertyTypeConstructor<T>
    {
        return this.typeCtor;
    }

    // this exist just for the sake of naming consistency on destructuring
    public get propType():Type
    {
        return this.type;
    }

    public createDefaultValue():T
    {
        return PropConfig.getValueInstance(this, this.defaultValue);
    }

    public createConverter():IPropertyConverter<T>
    {
        const converterCtor = this.converterCtor;
        const typeCtor = this.typeCtor;
        return converterCtor && typeCtor && new converterCtor(typeCtor);
    }

    public static getPropertyNames(valueInstance:any):Array<PropName>
    {
        if( !valueInstance )
            return [];

        const propertyMap = PropertyUtils.getPropertyMap(valueInstance);
        return propertyMap && propertyMap.getProperties().map(spec => spec.name) || [];
    }

    public static fillPropsConfigs(props:Map<string, PropConfig<any>>,
                                   defaultPropsValues:any):void
    {
        if( props && defaultPropsValues )
        {
            const propertyMap:PropertyMap = PropertyUtils.getPropertyMap(defaultPropsValues);

            if( propertyMap )
            {
                propertyMap.getProperties().forEach((propSpec:PropertySpec<any>) =>
                {
                    props.set(
                        propSpec.name,
                        new PropConfig<any>(propSpec, defaultPropsValues[propSpec.name])
                    );
                });
            }
        }
    }

    public static getValueInstance<T>(config:PropConfig<T>, definedPropValue:T):T
    {
        if( config.isArray && definedPropValue && definedPropValue[0] )
            return definedPropValue[0];

        if( definedPropValue )
            return definedPropValue;

        const typeCtor = config.typeCtor;
        return typeCtor.default ? typeCtor.default() : new typeCtor();
    }

    public static getSubPropConfig(root:PropConfig<any>, propPath:PropPath):PropConfig<any>
    {
        return propPath.reduce(root, (config, partAsString, partAsNumber) =>
        {
            // just skip array accessors in path
            if( !partAsString )
                return config;

            if( !config.propertiesConfigs.has(partAsString) )
                return null;

            return config.propertiesConfigs.get(partAsString);
        });
    }
}
